import {createReducer} from '@reduxjs/toolkit';
import {
  clearMapPin,
  fitExtent,
  pinMap,
  setCenterMarkerCoord,
  setDrawTool,
  setFeature,
  setIsOverZoomed,
  setMapExtent,
} from '@src/actions/map.js';
import {
  clearSearch,
  loadSearch,
  searchSingleItem,
} from '@src/actions/search.js';
import {DEFAULT_ZOOM} from '@src/constants';
import {getGeometry} from '@src/utils/filters.ts';

export const initialMapView = {
  /**
   * The map zoom level.
   * @type {number}
   */
  zoom: DEFAULT_ZOOM,
  /**
   * A hint to change extent to the map.
   * @type {Array<number>}
   */
  fitExtent: [],
};

export const initialState = {
  ...initialMapView,
  /**
   * The coordinate of a map pin
   * @type {Array<number>|null}
   */
  mapPin: null,

  /**
   * The current map extent/bounding box
   * @type {Array<number>|null}
   */
  extent: null,

  drawTool: {
    name: '',
    layer: '',
    target: '',
  },

  centerMarkerCoord: null,

  /**
   * The current map feature AOI
   * @type {import('geojson').Feature | null}
   */
  feature: null,

  isOverZoomed: false,
};

export default createReducer(initialState, builder => {
  builder.addCase(pinMap, (state, {payload}) => {
    state.mapPin = payload.coordinate;
  });

  builder.addCase(clearMapPin, state => {
    state.mapPin = initialState.mapPin;
  });

  builder.addCase(setMapExtent, (state, {payload}) => {
    state.extent = payload;
  });

  builder.addCase(fitExtent, (state, {payload}) => {
    state.fitExtent = payload;
  });

  builder.addCase(setDrawTool, (state, {payload}) => {
    state.drawTool = {...state.drawTool, ...payload};
  });

  builder.addCase(setCenterMarkerCoord, (state, {payload: point}) => {
    state.centerMarkerCoord = point;
  });

  builder.addCase(clearSearch, state => {
    state.centerMarkerCoord = null;
  });

  builder.addCase(setFeature, (state, {payload: feature}) => {
    state.feature = feature ? {...feature, id: feature.id ?? Date.now()} : null;
  });

  builder.addCase(setIsOverZoomed, (state, {payload}) => {
    state.isOverZoomed = payload;
  });

  builder.addCase(searchSingleItem, (state, {payload: item}) => {
    state.feature = {
      type: 'Feature',
      geometry: item.geometry,
      properties: {},
      id: item.id,
    };
  });

  builder.addCase(loadSearch, (state, {payload: search}) => {
    const geometry = search.hydratedGeometry || getGeometry(search.filter);

    state.feature = {
      type: 'Feature',
      geometry: geometry,
      properties: {},
      id: search.id,
    };
  });
});
